import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { ApiError, unwrap } from '@/api/envelope';
import {
  createCategory,
  updateCategory,
  type DocumentCategoryRow,
  type DocumentCategoryUpdate,
} from '@/api/generated';
import { applyServerErrors } from '@/api/serverErrors';
import { UnsavedChangesDialog } from '@/components/form/UnsavedChangesDialog';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Field } from '@/components/ui/Field';
import { useToast } from '@/components/ui/useToast';

import {
  CATEGORY_DESCRIPTION_MAX,
  CATEGORY_NAME_MAX,
  categoryFormSchema,
  type CategoryFormValues,
} from './documentSchemas';
import { useInvalidateDocuments } from './useDocuments';

/** The server's field names to the form's, in page order. They match. */
const SERVER_FIELDS = { name: 'name', description: 'description' } as const;

interface CategoryDialogProps {
  open: boolean;
  /** The category being renamed; absent for New Category. */
  category?: DocumentCategoryRow | null;
  onClose: () => void;
  /** The row as the backend answered, so a caller can select it at once. */
  onSaved: (row: DocumentCategoryRow) => void;
}

/**
 * New Category and Rename in one form (P9, PB10): opened from the upload
 * dialog's category select and from the Categories page.
 *
 * The Invite user dialog's shape: a form in the controlled dialog, which
 * stays open with everything typed when the save is refused and closes when
 * it succeeds. The two refusals the backend makes about a category - a
 * duplicate (409) and the reserved name "Uncategorized" (422) - are both
 * about the name, so both land under it in the backend's words.
 */
export function CategoryDialog({
  open,
  category,
  onClose,
  onSaved,
}: CategoryDialogProps) {
  const renaming = Boolean(category);
  const invalidate = useInvalidateDocuments();
  const toast = useToast();

  const id = useId();
  const formId = `${id}-form`;

  const [failure, setFailure] = useState<string | null>(null);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const alertRef = useRef<HTMLDivElement>(null);

  const {
    register,
    control,
    handleSubmit,
    setError,
    setFocus,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<CategoryFormValues>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: {
      name: category?.name ?? '',
      description: category?.description ?? '',
    },
  });

  // Counted as typed, so the limit is seen before Save refuses it.
  const nameTyped = useWatch({ control, name: 'name' }).length;
  const descriptionTyped = useWatch({ control, name: 'description' }).length;

  useEffect(() => {
    if (failure) alertRef.current?.focus();
  }, [failure]);

  const askToClose = () => {
    if (save.isPending) return;
    if (isDirty) setConfirmingClose(true);
    else onClose();
  };

  const save = useMutation({
    mutationFn: async (values: CategoryFormValues): Promise<DocumentCategoryRow> => {
      if (category?.id) {
        // Only what changed, so the backend's audit entry names just that.
        const body: DocumentCategoryUpdate = {};
        if (dirtyFields.name) body.name = values.name;
        if (dirtyFields.description) body.description = values.description || null;
        const reply = await updateCategory({
          path: { category_id: category.id },
          body,
          throwOnError: true,
        });
        return unwrap(reply.data);
      }
      const reply = await createCategory({
        body: { name: values.name, description: values.description || null },
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    onMutate: () => setFailure(null),
    onSuccess: async (row) => {
      onSaved(row);
      onClose();
      toast.show({ message: renaming ? 'Category renamed.' : 'Category created.' });
      // The tabs, the select and the Categories page all carry the names (I7).
      await invalidate();
    },
    onError: (error) => {
      const { remainder } = applyServerErrors(
        error,
        { setError, setFocus },
        SERVER_FIELDS,
        {
          conflictField: 'name',
        },
      );
      if (!remainder) return;
      // A 422 the backend words without a field name ("The name
      // "Uncategorized" is reserved") is still about the name.
      if (error instanceof ApiError && error.status === 422) {
        setError('name', { type: 'server', message: remainder });
        setFocus('name');
        return;
      }
      setFailure(remainder);
    },
  });

  const submit = handleSubmit((values) => {
    if (renaming && !isDirty) {
      onClose();
      return;
    }
    save.mutate(values);
  });

  return (
    <Dialog
      open={open}
      onClose={askToClose}
      title={renaming ? 'Rename category' : 'New category'}
      description={
        renaming
          ? 'Documents in this category keep their place under the new name.'
          : 'Documents can be put in this category as soon as it is created.'
      }
      testId="category-dialog"
      actions={
        <>
          <Button
            variant="secondary"
            onClick={askToClose}
            disabled={save.isPending}
            data-testid="category-dialog-cancel-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            loading={save.isPending}
            data-testid="category-dialog-save-button"
          >
            {renaming ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <Stack
        component="form"
        id={formId}
        spacing={2.5}
        aria-label={renaming ? 'Rename category' : 'New category'}
        data-testid="category-dialog-form"
        noValidate
        onSubmit={(event) => {
          // Opened from inside another form's category select: React bubbles
          // a submit through a portal, and the outer form must not hear it.
          event.stopPropagation();
          void submit(event);
        }}
        sx={{ pt: 0.5 }}
      >
        {failure ? (
          <Alert
            severity="error"
            role="alert"
            ref={alertRef}
            tabIndex={-1}
            data-testid="category-dialog-error"
          >
            {failure}
          </Alert>
        ) : null}

        <Field
          label="Name"
          hint={`${nameTyped} of ${CATEGORY_NAME_MAX} characters`}
          error={errors.name?.message}
          testId="category-dialog-name"
        >
          <TextField {...register('name')} size="small" fullWidth />
        </Field>

        <Field
          label="Description"
          hint={`${descriptionTyped} of ${CATEGORY_DESCRIPTION_MAX} characters`}
          error={errors.description?.message}
          testId="category-dialog-description"
        >
          <TextField
            {...register('description')}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
        </Field>
      </Stack>

      <UnsavedChangesDialog
        blocked={confirmingClose}
        stay={() => setConfirmingClose(false)}
        leave={() => {
          setConfirmingClose(false);
          onClose();
        }}
      />
    </Dialog>
  );
}
